import { useState, useEffect, useCallback } from 'react';
import { Package, ChevronDown, ChevronUp, Download, Mail, Clock, AlertCircle, Archive, ArchiveRestore } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'design_review', label: 'Design Review', color: 'text-amber-400 bg-amber-400/10' },
  { value: 'production', label: 'Production', color: 'text-blue-400 bg-blue-400/10' },
  { value: 'shipped', label: 'Shipped', color: 'text-purple-400 bg-purple-400/10' },
  { value: 'completed', label: 'Completed', color: 'text-green-400 bg-green-400/10' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-rose-400 bg-rose-400/10' },
];

function getStatusStyle(status) {
  return STATUS_OPTIONS.find(s => s.value === status)?.color || 'text-slate-400 bg-slate-400/10';
}

function getStatusLabel(status) {
  return STATUS_OPTIONS.find(s => s.value === status)?.label || status;
}

function getTimeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function OrderRow({ order, onStatusChange, onArchive }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/orders/${order.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_status: newStatus }),
      });
      if (res.ok) onStatusChange(await res.json());
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleArchive = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/orders/${order.id}/archive`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ archived: !order.archived }),
      });
      if (res.ok) onArchive(order.id);
    } catch (err) {
      console.error('Archive failed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const isPaid = order.payment_status === 'paid';

  return (
    <div data-testid="order-row" className="bg-[#111111] border border-white/5 rounded-xl hover:bg-white/[0.03]">
      <div className="p-4 flex items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {order.cropped_logo_path ? (
              <img src={`${API}/api/files/${order.cropped_logo_path}`} alt="Logo" className="w-10 h-10 rounded-lg object-contain border border-white/10 bg-white" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"><Package className="w-4 h-4 text-slate-600" /></div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white truncate">{order.order_number}</span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${getStatusStyle(order.order_status)}`}>{getStatusLabel(order.order_status)}</span>
              {!isPaid && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-orange-400 bg-orange-400/10">Unpaid</span>}
            </div>
            <p className="text-xs text-slate-500 truncate mt-0.5">{order.product_title} — {order.customer_email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-white">£{order.total_price?.toFixed(2)}</span>
          <div className="flex items-center gap-1 text-xs text-slate-600"><Clock className="w-3 h-3" />{getTimeAgo(order.created_at)}</div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-white/5 pt-4 space-y-4" data-testid="order-expanded">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-1">Customer</p>
              <p className="text-sm text-white">{order.customer_name || '—'}</p>
              <div className="flex items-center gap-1.5 mt-1"><Mail className="w-3 h-3 text-[#00E5FF]" /><span className="text-xs text-[#00B4D8]">{order.customer_email}</span></div>
              {order.phone && <p className="text-xs text-slate-400 mt-0.5">Tel: {order.phone}</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-1">Pricing</p>
              <p className="text-sm text-white">£{order.total_price?.toFixed(2)}</p>
              {order.discount_percent > 0 && <p className="text-xs text-green-400 mt-0.5">{order.discount_percent}% discount{order.discount_code ? ` (${order.discount_code})` : ''}</p>}
              {order.shipping_cost > 0 && <p className="text-xs text-slate-400 mt-0.5">Shipping: £{order.shipping_cost?.toFixed(2)}</p>}
            </div>
          </div>

          {order.shipping_address && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-1">Shipping Address</p>
              <div className="text-xs text-slate-300 leading-relaxed">
                <p>{order.shipping_address.full_name}</p>
                <p>{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.postcode}</p>
                <p>{order.shipping_address.country}</p>
              </div>
            </div>
          )}

          {order.selected_options?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Selected Options</p>
              <div className="flex flex-wrap gap-1.5">
                {order.selected_options.map((opt, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-slate-300">
                    {opt.label}{opt.price_modifier > 0 ? ` (+£${opt.price_modifier})` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {order.selected_color && (
            <div className="flex items-center gap-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-600">Colour:</p>
              <div className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: order.selected_color.hex_code }} />
              <span className="text-xs text-slate-300">{order.selected_color.name}</span>
            </div>
          )}

          {order.active_views?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Print Placements</p>
              <div className="flex flex-wrap gap-1.5">
                {order.active_views.map((v, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-[#00E5FF]/5 border border-[#00E5FF]/10 text-[#00E5FF]">{v.name || v.id || v}</span>
                ))}
              </div>
            </div>
          )}

          {/* Per-View Logo Placements */}
          {order.view_logos && order.view_logos.length > 0 ? (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Logo Placements by View</p>
              <div className="space-y-3">
                {order.view_logos.map((vl, vlIdx) => (
                  <div key={vlIdx} className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-[#00E5FF]">{vl.view_id}</span>
                      {vl.original_width > 0 && <span className="text-[10px] text-slate-500">{vl.original_width}px</span>}
                      {(vl.print_quality || (vl.original_width > 0 ? (vl.original_width >= 2000 ? 'good' : vl.original_width >= 1000 ? 'fair' : 'poor') : null)) && (() => {
                        const q = vl.print_quality || (vl.original_width >= 2000 ? 'good' : vl.original_width >= 1000 ? 'fair' : 'poor');
                        const styles = { good: 'text-green-400 bg-green-500/10 border-green-500/20', fair: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', poor: 'text-rose-400 bg-rose-500/10 border-rose-500/20', unknown: 'text-slate-400 bg-slate-500/10 border-slate-500/20' };
                        const labels = { good: 'Good Quality', fair: 'Fair — May Need Touch-up', poor: 'Poor Quality', unknown: 'Unknown' };
                        return <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${styles[q] || styles.unknown}`}>{labels[q] || q}</span>;
                      })()}
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-lg relative overflow-hidden flex-shrink-0">
                        <div className="absolute border border-dashed border-[#00E5FF]/30 rounded-sm" style={{ left: '5%', top: '5%', width: '90%', height: '90%' }}>
                          {vl.cropped_logo_path && (
                            <div className="absolute" style={{ left: `${vl.logo_placement?.x || 0}%`, top: `${vl.logo_placement?.y || 0}%`, width: `${vl.logo_placement?.width || 100}%`, height: `${vl.logo_placement?.height || 100}%` }}>
                              <img src={`${API}/api/files/${vl.cropped_logo_path}`} alt={`${vl.view_id} placement`} className="w-full h-full object-contain" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <span className="text-[10px] text-slate-600">X offset</span><span className="text-xs text-white font-mono">{vl.logo_placement?.x || 0}%</span>
                          <span className="text-[10px] text-slate-600">Y offset</span><span className="text-xs text-white font-mono">{vl.logo_placement?.y || 0}%</span>
                          <span className="text-[10px] text-slate-600">Width</span><span className="text-xs text-white font-mono">{vl.logo_placement?.width || 100}%</span>
                          <span className="text-[10px] text-slate-600">Height</span><span className="text-xs text-white font-mono">{vl.logo_placement?.height || 100}%</span>
                        </div>
                        <div className="flex gap-2 mt-2">
                          {vl.original_logo_path && <a href={`${API}/api/files/${vl.original_logo_path}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-400 hover:text-[#00E5FF] transition-colors"><Download className="w-3 h-3 inline mr-0.5" />Original</a>}
                          {vl.cropped_logo_path && <a href={`${API}/api/files/${vl.cropped_logo_path}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-400 hover:text-[#00E5FF] transition-colors"><Download className="w-3 h-3 inline mr-0.5" />Cropped</a>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Legacy single logo placement */}
              {order.logo_placement && order.cropped_logo_path && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Logo Position (within print zone)</p>
                  <div className="flex items-start gap-4">
                    <div className="w-24 h-24 bg-white/5 border border-white/10 rounded-lg relative overflow-hidden flex-shrink-0" data-testid="placement-preview">
                      <div className="absolute border border-dashed border-[#00E5FF]/30 rounded-sm"
                        style={{ left: '5%', top: '5%', width: '90%', height: '90%' }}>
                        <div className="absolute"
                          style={{ left: `${order.logo_placement.x}%`, top: `${order.logo_placement.y}%`, width: `${order.logo_placement.width}%`, height: `${order.logo_placement.height}%` }}>
                          <img src={`${API}/api/files/${order.cropped_logo_path}`} alt="Placement" className="w-full h-full object-contain" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <span className="text-[10px] text-slate-600">X offset</span><span className="text-xs text-white font-mono">{order.logo_placement.x}%</span>
                      <span className="text-[10px] text-slate-600">Y offset</span><span className="text-xs text-white font-mono">{order.logo_placement.y}%</span>
                      <span className="text-[10px] text-slate-600">Width</span><span className="text-xs text-white font-mono">{order.logo_placement.width}%</span>
                      <span className="text-[10px] text-slate-600">Height</span><span className="text-xs text-white font-mono">{order.logo_placement.height}%</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Design Assets</p>
            <div className="flex flex-wrap gap-2">
              {order.original_logo_path && (
                <a href={`${API}/api/files/${order.original_logo_path}`} target="_blank" rel="noopener noreferrer" data-testid="download-original"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-[#00E5FF] hover:border-[#00E5FF]/30 transition-colors">
                  <Download className="w-3 h-3" /> Original Hi-Res
                </a>
              )}
              {order.cropped_logo_path && (
                <a href={`${API}/api/files/${order.cropped_logo_path}`} target="_blank" rel="noopener noreferrer" data-testid="download-cropped"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-[#00E5FF] hover:border-[#00E5FF]/30 transition-colors">
                  <Download className="w-3 h-3" /> Cropped Logo
                </a>
              )}
              {!order.original_logo_path && !order.cropped_logo_path && (!order.view_logos || order.view_logos.length === 0) && (
                <div className="flex items-center gap-1.5 text-xs text-slate-600"><AlertCircle className="w-3 h-3" /> No assets uploaded</div>
              )}
            </div>
          </div>

          {isPaid && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Update Status</p>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => handleStatusUpdate(opt.value)} disabled={updating || order.order_status === opt.value}
                    data-testid={`status-btn-${opt.value}`}
                    className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors disabled:opacity-30 ${
                      order.order_status === opt.value ? `${opt.color} border-current` : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Archive Button */}
          <div className="pt-2 border-t border-white/5">
            <button onClick={handleArchive} disabled={updating} data-testid={order.archived ? 'unarchive-btn' : 'archive-btn'}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/30 transition-colors disabled:opacity-50">
              {order.archived ? <><ArchiveRestore className="w-3 h-3" /> Unarchive</> : <><Archive className="w-3 h-3" /> Archive Order</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrderManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/orders?archived=${showArchived}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setOrders(await res.json());
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusChange = (updated) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
  };

  const handleArchive = (orderId) => {
    setOrders(prev => prev.filter(o => o.id !== orderId));
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.order_status === filter);
  const paidOrders = orders.filter(o => o.payment_status === 'paid');

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Orders</h1>
          <p className="text-sm text-slate-500 mt-0.5">{paidOrders.length} paid / {orders.length} total{showArchived ? ' (archived)' : ''}</p>
        </div>
        <button onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived"
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
            showArchived ? 'border-amber-400/30 text-amber-400 bg-amber-400/5' : 'border-white/10 text-slate-500 hover:text-white'
          }`}>
          <Archive className="w-3 h-3" />
          {showArchived ? 'Viewing Archived' : 'Show Archived'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="order-filters">
        {[{ value: 'all', label: 'All' }, ...STATUS_OPTIONS].map(opt => (
          <button key={opt.value} onClick={() => setFilter(opt.value)}
            data-testid={`filter-${opt.value}`}
            className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
              filter === opt.value ? 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5' : 'border-white/10 text-slate-500 hover:text-white'
            }`}>
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading orders...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl">
          <Package className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{filter === 'all' ? (showArchived ? 'No archived orders' : 'No orders yet') : `No ${getStatusLabel(filter).toLowerCase()} orders`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(order => (
            <OrderRow key={order.id} order={order} onStatusChange={handleStatusChange} onArchive={handleArchive} />
          ))}
        </div>
      )}
    </div>
  );
}
